/**
 * PatternDifficultyMeter — overlay panel that shows the hand-aware
 * pattern difficulty for the section of the map currently in view
 * (a ~4-beat window around the playhead) plus the overall map score
 * AND a full-song heatmap with playhead + click-to-seek.
 *
 * Reads the PatternDifficultyReport pushed into the store by
 * usePatternDifficulty + editorStore.refreshPatternDifficulty. The heavy
 * heatmap canvas is repainted only when the report (or size) changes;
 * the playhead and the local-window bars update imperatively in a single
 * rAF loop so React doesn't re-render at 60fps.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { useEditorStore, getNoteColorsFromSettings } from '../../store/editorStore';
import {
  difficultyToColor,
  ratingTone,
  type HandMetrics,
  type PatternDifficultyReport,
} from '../../api/patternDifficulty';
import { seekAudio } from '../../audio/playbackController';
import { color as ui, font } from '../ui/theme';

// Beats either side of the playhead used for the "now" window in sub-bars.
const LOCAL_WINDOW_HALF_BEATS = 4;

// Heatmap canvas geometry.
const HEATMAP_HEIGHT = 28;

function avgInWindow(curve: number[], centerBeat: number): number {
  if (!curve.length) return 0;
  const lo = Math.max(0, Math.floor(centerBeat - LOCAL_WINDOW_HALF_BEATS));
  const hi = Math.min(curve.length - 1, Math.ceil(centerBeat + LOCAL_WINDOW_HALF_BEATS));
  if (hi < lo) return 0;
  let s = 0;
  for (let i = lo; i <= hi; i++) s += curve[i];
  return s / (hi - lo + 1);
}

type Rating = 'Easy' | 'Normal' | 'Hard' | 'Expert' | 'Master' | 'Insane';

function localRating(score: number): Rating {
  if (score < 0.15) return 'Easy';
  if (score < 0.3) return 'Normal';
  if (score < 0.5) return 'Hard';
  if (score < 0.7) return 'Expert';
  if (score < 0.85) return 'Master';
  return 'Insane';
}

/** Convert a song time (seconds) to raw beat using the report's BPM. */
function timeToRawBeat(timeSeconds: number, report: PatternDifficultyReport, fallbackBpm: number): number {
  const bpm = report.bpm || fallbackBpm || 120;
  return Math.max(0, (timeSeconds / 60) * bpm);
}

/** Convert a raw beat to song time (seconds). */
function rawBeatToTime(rawBeat: number, report: PatternDifficultyReport, fallbackBpm: number): number {
  const bpm = report.bpm || fallbackBpm || 120;
  if (bpm <= 0) return 0;
  return Math.max(0, (rawBeat / bpm) * 60);
}

export default function PatternDifficultyMeter() {
  const report = useEditorStore(s => s.patternDifficulty);
  const loading = useEditorStore(s => s.patternDifficultyLoading);
  const setCurrentTime = useEditorStore(s => s.setCurrentTime);
  // Live note-color settings — the swatches in the sub-bars reflect whatever
  // the user has configured in Gameplay Settings so the meter always matches
  // what they see in the 3D viewport.
  const gameplaySettings = useEditorStore(s => s.gameplaySettings);
  const noteColors = getNoteColorsFromSettings(gameplaySettings);

  // Local-window bar refs (updated each frame in the rAF loop).
  const overallBarRef = useRef<HTMLDivElement>(null);
  const overallPctRef = useRef<HTMLSpanElement>(null);
  const overallChipRef = useRef<HTMLSpanElement>(null);
  const leftBarRef = useRef<HTMLDivElement>(null);
  const rightBarRef = useRef<HTMLDivElement>(null);
  const greenBarRef = useRef<HTMLDivElement>(null);
  const bothBarRef = useRef<HTMLDivElement>(null);
  const leftValRef = useRef<HTMLSpanElement>(null);
  const rightValRef = useRef<HTMLSpanElement>(null);
  const greenValRef = useRef<HTMLSpanElement>(null);
  const bothValRef = useRef<HTMLSpanElement>(null);

  // Heatmap canvas + playhead.
  const heatmapCanvasRef = useRef<HTMLCanvasElement>(null);
  const heatmapWrapperRef = useRef<HTMLDivElement>(null);
  const playheadRef = useRef<HTMLDivElement>(null);

  const rafRef = useRef<number>(0);

  // ── Repaint the heatmap canvas whenever the report (or its size) changes ──
  const paintHeatmap = useCallback(() => {
    const canvas = heatmapCanvasRef.current;
    const wrapper = heatmapWrapperRef.current;
    if (!canvas || !wrapper || !report || report.combined.length === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const w = wrapper.clientWidth;
    const h = HEATMAP_HEIGHT;
    if (w <= 0) return;

    if (canvas.width !== Math.floor(w * dpr) || canvas.height !== Math.floor(h * dpr)) {
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Background track (visible where the song has no notes).
    ctx.fillStyle = 'rgba(15, 15, 30, 0.7)';
    ctx.fillRect(0, 0, w, h);

    // Heatmap: one pixel column per pixel of width, sample the combined curve.
    const total = report.combined.length;
    for (let px = 0; px < w; px++) {
      const idx = Math.min(total - 1, Math.floor((px / w) * total));
      const value = report.combined[idx];
      ctx.fillStyle = difficultyToColor(value);
      ctx.fillRect(px, 0, 1, h);
    }

    // Subtle top/bottom dividers for visual contrast against the panel.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.fillRect(0, 0, w, 1);
    ctx.fillRect(0, h - 1, w, 1);
  }, [report]);

  useEffect(() => {
    paintHeatmap();
  }, [paintHeatmap]);

  // Re-paint when the panel resizes (ResizeObserver on the wrapper).
  useEffect(() => {
    const wrapper = heatmapWrapperRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(() => paintHeatmap());
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [paintHeatmap]);

  // ── Single rAF loop: local sub-bars + heatmap playhead position ──
  useEffect(() => {
    if (!report) return;

    const loop = () => {
      const state = useEditorStore.getState();
      const bpm = state.beatmap?.bpm || report.bpm || 120;
      const currentTime = state.playback.currentTime;
      // Backend curves are in RAW beat (time_ms/1000 * bpm/60); match that.
      const rawBeat = Math.max(0, (currentTime / 60) * bpm);

      // ── Sub-bars (windowed local averages) ──
      const localOverall = avgInWindow(report.combined, rawBeat);
      const localLeft = avgInWindow(report.left.difficulty, rawBeat);
      const localRight = avgInWindow(report.right.difficulty, rawBeat);
      const localGreen = avgInWindow(report.green.difficulty, rawBeat);
      const localBoth = avgInWindow(report.both.difficulty, rawBeat);

      if (overallBarRef.current) {
        overallBarRef.current.style.width = `${(localOverall * 100).toFixed(1)}%`;
        overallBarRef.current.style.background = difficultyToColor(localOverall);
      }
      if (overallPctRef.current) {
        overallPctRef.current.textContent = `${Math.round(localOverall * 100)}%`;
      }
      if (overallChipRef.current) {
        const r = localRating(localOverall);
        overallChipRef.current.textContent = r;
        const tone = ratingTone(r);
        overallChipRef.current.style.background = tone.bg;
        overallChipRef.current.style.color = tone.fg;
      }

      const applyHand = (
        bar: HTMLDivElement | null,
        val: HTMLSpanElement | null,
        v: number,
      ) => {
        if (bar) {
          bar.style.width = `${(v * 100).toFixed(1)}%`;
          bar.style.background = difficultyToColor(v);
        }
        if (val) val.textContent = `${Math.round(v * 100)}`;
      };
      applyHand(leftBarRef.current, leftValRef.current, localLeft);
      applyHand(rightBarRef.current, rightValRef.current, localRight);
      applyHand(greenBarRef.current, greenValRef.current, localGreen);
      applyHand(bothBarRef.current, bothValRef.current, localBoth);

      // ── Heatmap playhead ──
      // Position the playhead by ratio = rawBeat / total_beats.
      const total = report.combined.length;
      if (playheadRef.current && total > 0) {
        const ratio = Math.max(0, Math.min(1, rawBeat / total));
        playheadRef.current.style.left = `${(ratio * 100).toFixed(3)}%`;
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [report]);

  // ── Click-to-seek on the heatmap ──
  const handleHeatmapClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!report || report.combined.length === 0) return;
    const wrapper = heatmapWrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const targetBeat = ratio * report.combined.length;
    const bpm = useEditorStore.getState().beatmap?.bpm || report.bpm || 120;
    const newTime = rawBeatToTime(targetBeat, report, bpm);

    // Move both the audio element and the store in lockstep, mirroring what
    // Timeline.handleCanvasClick does for its waveform clicks.
    seekAudio(newTime);
    setCurrentTime(newTime);
  }, [report, setCurrentTime]);

  // ── Empty / loading state ────────────────────────────────────────────────
  if (!report) {
    return (
      <div style={panelStyle}>
        <div style={headerRow}>
          <span style={labelStyle}>PATTERN DIFFICULTY</span>
          <span style={loadingHintStyle}>
            {loading ? 'analyzing…' : 'load a beatmap to analyze'}
          </span>
        </div>
        <div style={emptyBarStyle} />
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      <div style={headerRow}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={labelStyle}>PATTERN DIFFICULTY</span>
          <span style={nowLabelStyle}>NOW</span>
          <span
            ref={overallChipRef}
            style={chipBaseStyle}
            title="AI-rated difficulty of the section around the playhead"
          >
            {report.rating}
          </span>
          <span style={overallNoteStyle}>
            AI: <strong style={{ color: ui.ink }}>{report.rating}</strong>{' '}
            <span style={{ color: ui.inkFaint }}>
              ({Math.round(report.overall_score * 100)}%)
            </span>
          </span>
        </div>
        <span ref={overallPctRef} style={overallPctStyle}>0%</span>
      </div>

      <div style={overallBarTrackStyle}>
        <div ref={overallBarRef} style={overallBarFillStyle} />
      </div>

      <div style={subRowStyle}>
        <HandSubBar
          label="Left Hand"
          color={noteColors.left_hand}
          barRef={leftBarRef}
          valRef={leftValRef}
          stats={report.left}
        />
        <HandSubBar
          label="Right Hand"
          color={noteColors.right_hand}
          barRef={rightBarRef}
          valRef={rightValRef}
          stats={report.right}
        />
        <HandSubBar
          label="Any Hand"
          color={noteColors.one_hand_special}
          barRef={greenBarRef}
          valRef={greenValRef}
          stats={report.green}
        />
        <HandSubBar
          label="Both Hands"
          color={noteColors.both_hands_special}
          barRef={bothBarRef}
          valRef={bothValRef}
          stats={report.both}
        />
      </div>

      {/* Full-song heatmap with playhead + click-to-seek */}
      <div
        ref={heatmapWrapperRef}
        onClick={handleHeatmapClick}
        style={heatmapWrapperStyle}
        title="Click to seek"
      >
        <canvas ref={heatmapCanvasRef} style={heatmapCanvasStyle} />
        <div ref={playheadRef} style={playheadStyle} />
      </div>
    </div>
  );
}

function HandSubBar({
  label,
  color,
  barRef,
  valRef,
  stats,
}: {
  label: string;
  color: string;
  barRef: React.RefObject<HTMLDivElement | null>;
  valRef: React.RefObject<HTMLSpanElement | null>;
  stats: HandMetrics;
}) {
  return (
    <div style={subColStyle}>
      <div style={subHeaderStyle}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            display: 'inline-block', width: 8, height: 8,
            borderRadius: 2, background: color,
          }} />
          {label}
        </span>
        <span style={subValStyle}>
          <span ref={valRef}>0</span>
          <span style={{ color: ui.inkFaint, marginLeft: 2 }}>%</span>
        </span>
      </div>
      <div style={subBarTrackStyle}>
        <div ref={barRef} style={subBarFillStyle} />
      </div>
      <div style={subStatsStyle}>
        peak {stats.peak_density.toFixed(1)} n/s
        <span style={{ color: '#4A4366', margin: '0 5px' }}>·</span>
        {stats.peak_speed.toFixed(1)} m/s
      </div>
    </div>
  );
}

// ── Styles (purple/glass theme matching the rest of the editor) ─────────────

const panelStyle: React.CSSProperties = {
  background: 'rgba(10, 5, 32, 0.78)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  borderRadius: 10,
  padding: '10px 14px 12px',
  color: ui.ink,
  fontFamily: font.sans,
  pointerEvents: 'auto',
  userSelect: 'none',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)',
};

const headerRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 6,
};

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  letterSpacing: 1.2,
  color: ui.inkDim,
  fontWeight: 600,
};

const nowLabelStyle: React.CSSProperties = {
  fontSize: 9,
  letterSpacing: 1,
  color: ui.inkFaint,
  fontWeight: 600,
  textTransform: 'uppercase',
  borderRight: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  paddingRight: 8,
  marginRight: -4,
};

const chipBaseStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  padding: '2px 9px',
  borderRadius: 6,
  background: '#EAF3DE',
  color: '#3B6D11',
  letterSpacing: 0.4,
};

const overallNoteStyle: React.CSSProperties = {
  fontSize: 10,
  color: ui.inkDim,
};

const overallPctStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: ui.ink,
  fontVariantNumeric: 'tabular-nums',
};

const overallBarTrackStyle: React.CSSProperties = {
  height: 7,
  borderRadius: 4,
  background: 'rgba(15, 15, 30, 0.6)',
  overflow: 'hidden',
  marginBottom: 10,
};

const overallBarFillStyle: React.CSSProperties = {
  height: '100%',
  width: '0%',
  background: '#97C459',
  borderRadius: 4,
  transition: 'background-color 0.15s linear',
};

const subRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: 10,
  marginBottom: 10,
};

const subColStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

const subHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontSize: 11,
  color: 'oklch(0.82 calc(0.12 * var(--accent-c)) var(--accent-h))',
};

const subValStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: ui.ink,
  fontVariantNumeric: 'tabular-nums',
};

const subBarTrackStyle: React.CSSProperties = {
  height: 4,
  borderRadius: 2,
  background: 'rgba(15, 15, 30, 0.6)',
  overflow: 'hidden',
};

const subBarFillStyle: React.CSSProperties = {
  height: '100%',
  width: '0%',
  background: '#97C459',
  borderRadius: 2,
  transition: 'background-color 0.15s linear',
};

const subStatsStyle: React.CSSProperties = {
  fontSize: 9,
  color: ui.inkFaint,
  fontVariantNumeric: 'tabular-nums',
};

const loadingHintStyle: React.CSSProperties = {
  fontSize: 10,
  color: ui.inkFaint,
  fontStyle: 'italic',
};

const emptyBarStyle: React.CSSProperties = {
  height: 7,
  borderRadius: 4,
  background: 'rgba(15, 15, 30, 0.6)',
};

// ── Song heatmap (full-song strip with playhead + click) ────────────────────

const heatmapWrapperStyle: React.CSSProperties = {
  position: 'relative',
  height: HEATMAP_HEIGHT,
  borderRadius: 4,
  overflow: 'hidden',
  cursor: 'pointer',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
};

const heatmapCanvasStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: HEATMAP_HEIGHT,
};

const playheadStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: '0%',
  width: 2,
  marginLeft: -1,
  background: '#FFFFFF',
  boxShadow: '0 0 6px rgba(255, 255, 255, 0.65)',
  pointerEvents: 'none',
};
